/**
 * Gestiona la cola FIFO de notificaciones.
 *
 * - Máximo MAX_VISIBLE toasts visibles al mismo tiempo.
 * - Las notificaciones de alta prioridad saltan la cola.
 * - Al cerrar un toast visible, el primero de la cola pasa a visible.
 * - Expone `queuedCount` para que el renderer muestre "N más".
 */
export class NotificationQueueManager {
  static MAX_VISIBLE = 4;

  #visible  = [];
  #queue    = [];
  #onChange = null;
  #nextId   = 0;

  constructor(onChange) {
    this.#onChange = onChange;
  }

  add(rawNotification) {
    // Asignar id único si el caller no lo proporcionó.
    // Necesario para que React use key={notif.id} sin warning de lista.
    const notification = rawNotification.id
      ? rawNotification
      : { ...rawNotification, id: `n-${++this.#nextId}-${Date.now()}` };

    const priority = notification.config?.priority ?? 0;

    if (this.#visible.length < NotificationQueueManager.MAX_VISIBLE) {
      this.#visible = this.#insertByPriority(this.#visible, notification);
    } else {
      // Alta prioridad (≥ 3) reemplaza el toast de menor prioridad visible
      const lowestVisible = [...this.#visible].sort(
        (a, b) => (a.config?.priority ?? 0) - (b.config?.priority ?? 0)
      )[0];

      if (priority >= 3 && (lowestVisible?.config?.priority ?? 0) < priority) {
        this.#queue   = this.#insertByPriority(this.#queue, lowestVisible);
        this.#visible = this.#visible.filter(n => n.id !== lowestVisible.id);
        this.#visible = this.#insertByPriority(this.#visible, notification);
      } else {
        this.#queue = this.#insertByPriority(this.#queue, notification);
      }
    }

    this.#emit();
  }

  remove(id) {
    this.#visible = this.#visible.filter(n => n.id !== id);

    if (this.#queue.length > 0) {
      const [next, ...rest] = this.#queue;
      this.#visible = [...this.#visible, next];
      this.#queue   = rest;
    }

    this.#emit();
  }

  clear() {
    this.#visible = [];
    this.#queue   = [];
    this.#emit();
  }

  #insertByPriority(list, item) {
    const priority = item.config?.priority ?? 0;
    const idx = list.findIndex(n => (n.config?.priority ?? 0) < priority);
    if (idx === -1) return [...list, item];
    return [...list.slice(0, idx), item, ...list.slice(idx)];
  }

  #emit() {
    this.#onChange({
      visible:     this.#visible,
      queuedCount: this.#queue.length,
    });
  }
}
